<template>
  <div class="locations-gmb-id" @scroll.passive="onScroll($event.srcElement, tabsElement)">
    <screen-header
      class="locations-gmb-id__header"
      :title="formattedLocationName"
      :breadcrumbs="breadcrumbs"
      :is-loading="isLoading"
      v-if="!hasError"
      :right-content="true"
    >
      <div class="locations-gmb-id__header__link-to-location__button" v-if="isLoading">
        <skeleton-input margin-left="24px" />
      </div>
      <v-popover
        placement="bottom-end"
        :autoHide="true"
        container=".locations-gmb-id__header"
        popoverInnerClass="popover-inner no-overflow"
        v-else-if="hasUrls"
      >
        <ui-button
          class="locations-gmb-id__header__cta"
          button="cta"
          variant="data"
          icon="external_link"
          :label="$t('locationsGmb.id.header.goToLocation.label')"
          :mobile-toggle="true"
        />
        <template slot="popover">
          <div class="locations-gmb-id__header__popover">
            <a
              :href="currentLocation.websiteUrl"
              class="locations-gmb-id__header__popover__link"
              v-if="currentLocation.websiteUrl"
              target="_blank"
            >
              {{ $t('locationsGmb.id.header.link.website') }}
            </a>
            <a
              :href="currentLocation.metadata.mapsUrl"
              class="locations-gmb-id__header__popover__link"
              v-if="currentLocation.metadata.mapsUrl"
              target="_blank"
            >
              {{ $t('locationsGmb.id.header.link.maps') }}
            </a>
            <a
              :href="currentLocation.metadata.serpUrl"
              class="locations-gmb-id__header__popover__link"
              v-if="currentLocation.metadata.serpUrl"
              target="_blank"
            >
              {{ $t('locationsGmb.id.header.link.serp') }}
            </a>
            <a
              :href="currentLocation.metadata.newReviewUrl"
              class="locations-gmb-id__header__popover__link"
              v-if="currentLocation.metadata.newReviewUrl"
              target="_blank"
            >
              {{ $t('locationsGmb.id.header.link.newReview') }}
            </a>
            <a :href="qaUrl" class="locations-gmb-id__header__popover__link" v-if="qaUrl.length > 0" target="_blank">
              {{ $t('locationsGmb.id.header.link.qa') }}
            </a>
          </div>
        </template>
      </v-popover>
    </screen-header>
    <screen-error
      v-if="hasError"
      :route="{ name: 'LocationsGmb', params: { account: { name: this.selectedGmbAccount } } }"
      :label="$t('locationsGmb.id.error.button.back')"
    />
    <screen-container direction="column" v-else>
      <screen-tabs
        :tabs="tabs"
        :element-fixed="elementFixed"
        :current-tab="currentTab"
        :is-loading="isLoading"
        @mounted="onTabsMounted"
        @click="setCurrentTab"
      />

      <location-gmb-home
        v-if="currentTab === 'home'"
        :is-loading="isLoading"
        :is-updating="isUpdating"
        :locale-lang="$i18n.locale"
        :current-location="currentLocation"
        :exceptionnal-hours-api="exceptionnalHoursApi"
        @updateOpenStatus="updateOpenStatus"
        @editExceptionalHours="editExceptionalHours"
      />
      <!-- General Section -->
      <location-gmb-general
        v-if="currentTab === 'general'"
        :is-loading="isLoading"
        :is-updating="isUpdating"
        :current-location="currentLocation"
        :current-client="currentClient"
        :countries="countries"
        :categories="categories"
        @editSimpleField="editSimpleField"
        @editAddress="editAddress"
        @editCoordinates="editCoordinates"
        @editOpeningHours="editOpeningHours"
        @editExceptionalHours="editExceptionalHours"
        @updateOpenStatus="updateOpenStatus"
      />
      <location-gmb-extra
        v-if="currentTab === 'extras'"
        :is-loading="isLoading"
        :current-location="currentLocation"
        :current-client="currentClient"
        :locations-search="locations"
        :current-location-mapping="currentLocationMapping"
        @editSimpleField="editSimpleField"
        @editLocationMapping="editLocationMapping"
      />
      <location-gmb-attribut
        v-if="currentTab === 'attributs'"
        :is-loading="isLoading"
        :current-location="currentLocation"
        :current-client="currentClient"
        :all-categorys="allCategorys"
        @editAttribute="editAttribute"
        @editCategory="editCategory"
        @editGmbAutocomplete="editGmbAutocomplete"
      />
      <location-gmb-photo
        v-if="currentTab === 'photos'"
        :is-loading="isLoading"
        :current-location="currentLocation"
        :current-location-medias="currentLocationMedias"
        :current-client="currentClient"
        @deletePhoto="showDeletePhotoModal"
        @addPhoto="showAddPhotoModal"
        @showPreview="showPreview"
        @flagClick="flagClick"
      />
    </screen-container>
    <modal-simple-field
      v-if="isEditing && editingModal === 'field'"
      :title="modalTitle"
      :is-updating="isUpdating"
      :data="modal.data"
      :label="modal.label"
      :obj-key="modal.objKey"
      :parameters="modal.parameters"
      @save="saveSimpleField"
      @saveMapping="saveMapping"
      @closed="closedModal"
      @search="searchLocations"
    />
    <modal-address
      v-if="isEditing && editingModal === 'address'"
      :title="modalTitle"
      :is-updating="isUpdating"
      :data="modal.data"
      @save="saveAddress"
      @closed="closedModal"
    />
    <modal-coordinates
      v-if="isEditing && editingModal === 'coordinates'"
      :title="modalTitle"
      :is-updating="isUpdating"
      :data="modal.data"
      @save="saveSimpleField"
      @closed="closedModal"
    />
    <modal-hours
      v-if="isEditing && editingModal === 'openingHours'"
      :title="modalTitle"
      :is-updating="isUpdating"
      :data="modal.data"
      :parameters="modal.parameters"
      @save="saveOpeningHours"
      @closed="closedModal"
    />
    <modal-exceptional-hours
      v-if="isEditing && editingModal === 'exceptionalHours'"
      :title="modalTitle"
      :is-updating="isUpdating"
      :data="modal.data"
      :parameters="modal.parameters"
      @save="saveExceptionalHours"
      @closed="closedModal"
    />
    <modal-gmb-autocomplete
      v-if="isEditing && editingModal === 'gmbAutocomplete'"
      :title="modalTitle"
      :is-updating="isUpdating"
      :data="modal.data"
      @save="saveGmbAutocomplete"
      @closed="closedModal"
    />
    <modal-gmb-photo
      v-if="editingModal === 'gmbPhoto'"
      :photo="modal.data"
      :type="modal.parameters.type"
      @closed="closedModal"
      @deletePhoto="deletePhoto"
      @save="deletePhoto"
    />
    <modal-media
      v-if="isEditing && editingModal === 'addPhoto'"
      :title="modalTitle"
      :is-updating="isUpdating"
      :data="modal.data"
      :parameters="modal.parameters"
      @add="addPhoto"
      @closed="closedModal"
    />
    <modal-gmb
      v-if="isEditing && editingModal === 'attributs'"
      :title="modalTitle"
      :is-updating="isUpdating"
      :data="modal.data"
      :parameters="modal.parameters"
      @save="saveAttributes"
      @closed="closedModal"
    />
    <modal-gmb-category
      v-if="isEditing && editingModal === 'category'"
      :title="modalTitle"
      :is-updating="isUpdating"
      :data="modal.data"
      :parameters="modal.parameters"
      :current-location="currentLocation"
      @save="saveCategory"
      @closed="closedModal"
      @updateOptions="updateOptions"
    />
    <modal-confirm
      v-if="isEditing && editingModal === 'deletePhoto'"
      :parameters="modal.parameters"
      @save="deletePhoto(modal.parameters.photo)"
      @cancel="closedModal"
      @closed="closedModal"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapState, mapActions } from 'vuex'
import { recordFlagEvent } from '@/services/gmb.service'
import { onScroll } from '@/mixins/scroll.mixin'
import { notif } from '@/mixins/notification.mixin'
import ScreenHeader from '@/components/Screen/Header.vue'
import ScreenContainer from '@/components/Screen/Container.vue'
import ScreenTabs from '@/components/Screen/Tabs.vue'
import ScreenError from '@/components/Screen/Error.vue'
import LocationGmbHome from '@/components/LocationGmb/Home.vue'
import LocationGmbGeneral from '@/components/LocationGmb/General.vue'
import LocationGmbExtra from '@/components/LocationGmb/Extra.vue'
import LocationGmbAttribut from '@/components/LocationGmb/Attribut.vue'
import LocationGmbPhoto from '@/components/LocationGmb/Photo.vue'
import SkeletonInput from '@/components/Skeleton/Input.vue'
import UiButton from '@/components/UI/Button.vue'
import ModalSimpleField from '@/components/Modal/SimpleField.vue'
import ModalAddress from '@/components/Modal/Address.vue'
import ModalCoordinates from '@/components/Modal/Coordinates.vue'
import ModalHours from '@/components/Modal/Hours.vue'
import ModalExceptionalHours from '@/components/Modal/ExceptionalHours.vue'
import ModalGmbPhoto from '@/components/Modal/GmbPhoto.vue'
import ModalGmbAutocomplete from '@/components/Modal/GmbAutocomplete.vue'
import ModalMedia from '@/components/Modal/Media.vue'
import ModalGmb from '@/components/Modal/Gmb.vue'
import ModalGmbCategory from '@/components/Modal/GmbCategory.vue'
import ModalConfirm from '@/components/Modal/Confirm.vue'
import { timeFromNow } from '@/utils/hours.util'
import get from 'lodash/get'
import set from 'lodash/set'
import differenceBy from 'lodash/differenceBy'
import { importPicture, getCategories } from '@/services/gmb.service'
import { userRoles } from '@/config/permissions.config'
import { isUserAuthorized } from '@/config/permissions.config'

const initialModal = () => ({
  objKey: null,
  data: null,
  label: null,
  parameters: {
    type: 'text',
    mode: 'input',
    required: false,
    number: null,
    options: [],
  },
})
export default {
  name: 'LocationsIdGmb',
  components: {
    ScreenHeader,
    ScreenContainer,
    ScreenTabs,
    ScreenError,
    LocationGmbHome,
    LocationGmbGeneral,
    LocationGmbExtra,
    LocationGmbAttribut,
    LocationGmbPhoto,
    SkeletonInput,
    UiButton,
    ModalSimpleField,
    ModalAddress,
    ModalCoordinates,
    ModalHours,
    ModalExceptionalHours,
    ModalGmbAutocomplete,
    ModalGmbPhoto,
    ModalGmb,
    ModalMedia,
    ModalConfirm,
    ModalGmbCategory,
  },
  mixins: [onScroll, notif],
  data() {
    return {
      isLoading: false,
      isEditing: false,
      editingModal: '',
      modal: initialModal(),
      hasError: false,
      tabsElement: null,
      categories: [],
      userRoles,
      locationsSearch: {},
    }
  },
  async created() {
    this.isLoading = true
    if (!this.$route.params.currentTab) {
      this.setCurrentTab('home')
    }
    await Promise.all([
      await this.getLocations({ tableParams: null, search: this.search }),
      await this.getCurrentLocationsMapping({
        id: this.currentClientId,
        accountGmb: this.selectedGmbLocation,
        account: this.selectedGmbAccount,
      }),
      await this.loadLocation({
        locationName: this.selectedGmbLocation,
        currentAccountName: this.selectedGmbAccount,
      }),
      this.loadLocationMedias({
        locationName: this.selectedGmbLocation,
        currentAccountName: this.selectedGmbAccount,
      }),
      this.getExceptionnalHoursApi({
        locale: this.currentLocation?.address?.regionCode,
        year: new Date().getFullYear(),
      }),
    ])
    const {
      data: { results: categories },
    } = await getCategories({
      size: 5000,
      locale: this.$i18n.locale,
      regionCode: this.currentLocation.address.regionCode,
    })
    this.categories = categories
    this.isLoading = false
  },
  computed: {
    ...mapState({
      locations: state => state.location.locations,
      currentLocation: state => state.gmb.currentLocation,
      currentLocationMapping: state => state.location.currentLocationMapping,
      currentLocationMedias: state => state.gmb.currentLocationMedias,
      exceptionnalHoursApi: state => state.gmb.exceptionnalHoursApi,
      currentClient: state => state.user.currentClient,
      currentClientId: state => state.user.currentClient?.id,
      countries: state => state.user.countries,
    }),
    qaUrl() {
      if (this.currentLocation?.metadata?.serpUrl?.length > 0) {
        const id = new URL(this.currentLocation.metadata.serpUrl).searchParams.get('id')
        return `https://local.google.com/place?id=${id}&use=srp#lpqa=d,2`
      }
      return ''
    },
    selectedGmbAccount() {
      const routeParams = this.$route.params.name.split('/')
      return `${routeParams[0]}/${routeParams[1]}`
    },
    selectedGmbLocation() {
      return this.$route.params.name
    },
    currentTab() {
      return this.$route.params.currentTab
    },
    breadcrumbs() {
      return [
        {
          label: this.$t('locationsGmb.list.breadcrumb'),
          route: {
            name: 'LocationsGmb',
            params: {
              name: this.selectedGmbAccount,
            },
          },
        },
        {
          label: this.formattedLocationName,
          route: {
            name: 'LocationGmb',
            params: {
              name: this.currentLocation.name,
            },
          },
        },
      ]
    },
    tabs() {
      if (this.currentLocation.primaryCategory) {
        return [
          {
            label: this.$t('locationsGmb.id.tab.home'),
            key: 'home',
          },
          {
            label: this.$t('locationsGmb.id.tab.general'),
            key: 'general',
          },
          {
            label: this.$t('locationsGmb.id.tab.attribut'),
            key: 'attributs',
          },
          {
            label: this.$t('locationsGmb.id.tab.photos'),
            key: 'photos',
          },
          isUserAuthorized(userRoles.superAdmin)
            ? {
                label: this.$t('locationsGmb.id.tab.extras'),
                key: 'extras',
              }
            : {},
        ]
      }
      return [
        {
          label: this.$t('locationsGmb.id.tab.home'),
          key: 'home',
        },
        {
          label: this.$t('locationsGmb.id.tab.general'),
          key: 'general',
        },
        {
          label: this.$t('locationsGmb.id.tab.photos'),
          key: 'photos',
        },
        isUserAuthorized(userRoles.superAdmin)
          ? {
              label: this.$t('locationsGmb.id.tab.extras'),
              key: 'extras',
            }
          : {},
      ]
    },
    modalTitle() {
      return this.$t('common.modal.title.location', {
        location: this.currentLocation.locationName,
      })
    },
    hasUrls() {
      if (
        (typeof this.currentLocation.websiteUrl === 'string' && this.currentLocation.websiteUrl !== '') ||
        (typeof this.currentLocation.metadata?.mapsUrl === 'string' && this.currentLocation.metadata.mapsUrl !== '') ||
        (typeof this.currentLocation.metadata?.newReviewUrl === 'string' &&
          this.currentLocation.metadata.newReviewUrl !== '')
      ) {
        return true
      }
      return false
    },
    formattedLocationName() {
      return this.currentLocation.storeCode
        ? `${this.currentLocation.locationName} (${this.currentLocation.storeCode})`
        : this.currentLocation.locationName
    },
    allCategorys() {
      return _.unionBy(
        this.currentLocation.serviceList?.serviceItems || [],
        this.currentLocation.additionalCategories || [],
        this.currentLocation.primaryCategory,
        'categoryId'
      )
    },
  },
  methods: {
    ...mapActions({
      getCurrentLocationsMapping: 'location/getCurrentLocationsMapping',
      updateCurrentLocationsMapping: 'location/updateCurrentLocationsMapping',
      loadLocation: 'gmb/loadLocation',
      loadLocationMedias: 'gmb/loadLocationMedias',
      getExceptionnalHoursApi: 'gmb/getExceptionnalHoursApi',
      updateLocation: 'gmb/updateLocation',
      deleteLocationMedia: 'gmb/deleteLocationMedia',
      getLocations: 'location/getLocations',
    }),
    searchLocations(e) {
      if (this.modal.objKey === 'locationsMapping') {
        this.search = e
        this.getLocations({ tableParams: null, search: this.search }).then(() => {
          this.modal.parameters = {
            ...this.modal.parameters,
            options: this.locations,
          }
        })
      }
    },
    updateOptions(options) {
      this.modal.parameters.options = options
    },
    onTabsMounted(element) {
      this.tabsElement = element
    },
    setCurrentTab(tab) {
      this.$router.replace({ ...this.$route, params: { ...this.$route.params, currentTab: tab } })
    },
    formatedDate(date) {
      return timeFromNow(date, this.$i18n.locale, false)
    },
    editSimpleField(payload) {
      this.modal.objKey = payload.objKey
      this.modal.label = this.$t(`locationsGmb.id.modal.${payload.objKey}`)
      this.modal.data = get(this.currentLocation, payload.objKey)
      this.modal.parameters = payload.parameters
      this.isEditing = true
      this.editingModal = 'field'
      this.$nextTick(() => {
        this.$modal.show('modal-simple-field')
      })
    },
    editLocationMapping(payload) {
      this.modal.objKey = payload.objKey
      this.modal.label = this.$t(`locationsGmb.id.modal.${payload.objKey}`)
      this.modal.data = this.currentLocationMapping
      this.modal.parameters = payload.parameters
      this.isEditing = true
      this.editingModal = 'field'
      this.$nextTick(() => {
        this.$modal.show('modal-simple-field')
      })
    },
    editAddress(payload) {
      this.modal.data = payload.address
      this.isEditing = true
      this.editingModal = 'address'
      this.$nextTick(() => {
        this.$modal.show('modal-address')
      })
    },
    editCoordinates(payload) {
      this.modal.data = payload.coordinates
      this.isEditing = true
      this.editingModal = 'coordinates'
      this.$nextTick(() => {
        this.$modal.show('modal-coordinates')
      })
    },
    editOpeningHours(payload) {
      this.modal.data = payload.hours
      this.isEditing = true
      this.editingModal = 'openingHours'
      this.modal.parameters.type = 'gmb'
      this.$nextTick(() => {
        this.$modal.show('modal-hours')
      })
    },
    editExceptionalHours(payload) {
      this.modal.data = payload.exceptionalHour
      this.isEditing = true
      this.editingModal = 'exceptionalHours'
      this.modal.parameters.type = 'gmb'
      this.$nextTick(() => {
        this.$modal.show('modal-exceptional-hours')
      })
    },
    editAttribute(payload) {
      this.modal.data = payload.attribut
      this.modal.parameters.options = payload.refs
      this.isEditing = true
      this.editingModal = 'attributs'
      this.$nextTick(() => {
        this.$modal.show('modal-gmb')
      })
    },
    editCategory(payload) {
      const data = payload.services
      data?.forEach(service => {
        service.id = `${Math.random().toString(36).slice(2)}}`
      })
      this.modal.data = data || []
      this.modal.parameters.options = data || []
      this.modal.parameters.label = payload.displayName
      this.modal.parameters.categoryId = payload.categoryId
      this.isEditing = true
      this.editingModal = 'category'
      this.$nextTick(() => {
        this.$modal.show('modal-gmb-category')
      })
    },
    editGmbAutocomplete(serviceArea) {
      this.modal.data = serviceArea
      this.isEditing = true
      this.editingModal = 'gmbAutocomplete'
      this.$nextTick(() => {
        this.$modal.show('modal-gmb-autocomplete')
      })
    },
    saveSimpleField(params) {
      this.isUpdating = true
      this.updateLocation({
        data: set({}, Object.keys(params.objKey)[0], Object.values(params.objKey)[0]),
        currentAccountName: this.selectedGmbAccount,
      })
        .then(() => {
          this.$modal.hide('modal-simple-field')
          this.closedModal()
          this.notificationSuccess('location', this.currentLocation.locationName)
        })
        .catch(() => this.notificationError())
    },
    saveMapping(params) {
      this.isUpdating = true
      const ids = params.objKey.locationsMapping.map(location => location.id)
      this.updateCurrentLocationsMapping({
        id: this.currentClientId,
        accountGmb: this.selectedGmbLocation,
        account: this.selectedGmbAccount,
        placeId: this.currentLocation.locationKey.placeId,
        locationsId: ids,
      })
        .then(() => {
          this.$modal.hide('modal-simple-field')
          this.closedModal()
          this.notificationSuccess('location', this.currentLocation.locationName)
        })
        .catch(() => this.notificationError())
    },
    saveOpeningHours(params) {
      this.isUpdating = true
      this.updateLocation({ data: { regularHours: params.objKey }, currentAccountName: this.selectedGmbAccount })
        .then(() => {
          this.$modal.hide('modal-hours')
          this.closedModal()
          this.notificationSuccess('location', this.currentLocation.locationName)
        })
        .catch(() => this.notificationError())
    },
    saveExceptionalHours(params) {
      this.isUpdating = true
      let eoh = [...this.currentLocation.specialHours.specialHourPeriods] || []
      if (params.status === 'create') {
        eoh.push(params.objKey)
      } else if (params.status === 'delete') {
        eoh = eoh.filter(hour => hour.id !== params.objKey.id)
      } else if (params.status === 'update') {
        eoh = eoh.map(hour => (hour.id === params.objKey.id ? params.objKey : hour))
      }
      this.updateLocation({
        data: { specialHours: { specialHourPeriods: eoh } },
        currentAccountName: this.selectedGmbAccount,
      })
        .then(() => {
          this.$modal.hide('modal-exceptional-hours')
          this.closedModal()
          this.notificationSuccess('location', this.currentLocation.locationName)
        })
        .catch(() => this.notificationError())
    },
    saveAddress(params) {
      this.isUpdating = true
      const addressObj = {
        addressLines: [],
        locality: params.objKey.city,
        postalCode: params.objKey.postalCode,
        regionCode: params.objKey.countryCode,
      }
      if (params.objKey.street1 !== '') {
        addressObj.addressLines.push(params.objKey.street1)
      }
      if (params.objKey.street2 !== '') {
        addressObj.addressLines.push(params.objKey.street2)
      }
      this.updateLocation({ data: { address: addressObj }, currentAccountName: this.selectedGmbAccount })
        .then(() => {
          this.$modal.hide('modal-address')
          this.closedModal()
          this.notificationSuccess('location', this.currentLocation.locationName)
        })
        .catch(() => this.notificationError())
    },
    saveAttributes(params) {
      this.isUpdating = true
      const attrs = differenceBy(this.currentLocation.attributes, params.objKey, 'group')
      const filtered = attrs.filter(
        attr => params.attributesToRemove.findIndex(value => value === attr.attributeId) === -1
      )
      this.updateLocation({
        data: { attributes: [...filtered, ...params.objKey] },
        currentAccountName: this.selectedGmbAccount,
      })
        .then(() => {
          this.$modal.hide('modal-gmb')
          this.closedModal()
          this.notificationSuccess('location', this.currentLocation.locationName)
        })
        .catch(() => this.notificationError())
    },
    saveCategory({ currentServices, categoryId, displayName }) {
      const index = this.allCategorys.findIndex(category => category.categoryId === categoryId)
      this.allCategorys.splice(index, 1, {
        displayName,
        categoryId,
        services: [...currentServices],
      })
      this.isUpdating = true
      const data = {
        serviceList: {
          name: `${this.currentLocation.name}/serviceList`,
          serviceItems: [...this.allCategorys],
        },
      }
      this.updateLocation({
        data,
        currentAccountName: this.selectedGmbAccount,
      })
        .then(() => {
          this.$modal.hide('modal-gmb-category')
          this.closedModal()
          this.notificationSuccess('location', this.currentLocation.locationName)
        })
        .catch(() => this.notificationError())
    },
    saveGmbAutocomplete(params) {
      this.isUpdating = true
      this.updateLocation({ data: params, currentAccountName: this.selectedGmbAccount })
        .then(() => {
          this.$modal.hide('modal-gmb-autocomplete')
          this.closedModal()
          this.notificationSuccess('location', this.currentLocation.locationName)
        })
        .catch(() => this.notificationError())
    },
    showPreview(params) {
      this.modal.data = params.photo
      this.modal.parameters.type = params.type
      this.editingModal = 'gmbPhoto'
      this.$nextTick(() => {
        this.$modal.show('modal-gmb-photo')
      })
    },
    flagClick(url) {
      recordFlagEvent(this.currentClientId, url, this.selectedGmbAccount)
    },
    showDeletePhotoModal(photo) {
      this.isEditing = true
      this.modal.parameters.photo = photo
      this.modal.parameters.mode = 'confirmOwnerwPictureDeletion'
      this.editingModal = 'deletePhoto'
      this.$nextTick(() => {
        this.$modal.show('modal-confirm')
      })
    },
    async deletePhoto({ name: locationMediaId }) {
      try {
        await this.deleteLocationMedia({
          accountName: this.selectedGmbAccount,
          locationMediaId,
        })
        await this.loadLocationMedias({
          locationName: this.selectedGmbLocation,
          currentAccountName: this.selectedGmbAccount,
        })
        this.notificationSuccess('location', this.currentLocation.locationName)
      } catch (err) {
        this.notificationError()
      }
      this.closedModal()
    },
    updateOpenStatus(status) {
      this.isUpdating = true
      this.updateLocation({ data: { openInfo: { status } }, currentAccountName: this.selectedGmbAccount })
        .then(() => {
          this.notificationSuccess('location', this.currentLocation.locationName)
          this.closedModal()
        })
        .catch(() => this.notificationError())
    },
    showAddPhotoModal({ category }) {
      this.isEditing = true
      this.editingModal = 'addPhoto'
      this.modal.parameters.category = category
      this.modal.parameters.type = 'locationPictures'
      this.$nextTick(() => {
        this.$modal.show('modal-media')
      })
    },
    async addPhoto(params) {
      this.isUpdating = true
      try {
        await importPicture({
          clientId: this.currentClientId,
          account: this.selectedGmbAccount,
          locationName: this.selectedGmbLocation,
          mediaCategory: this.modal.parameters.category,
          file: params.image,
          name: this.currentLocation.locationName,
        })
        this.notificationSuccess('location', this.currentLocation.locationName)
        await this.loadLocationMedias({
          locationName: this.selectedGmbLocation,
          currentAccountName: this.selectedGmbAccount,
        })
      } catch (err) {
        this.notificationError()
      }
      this.$modal.hide('modal-media')
      this.closedModal()
      this.isUpdating = false
    },
    closedModal() {
      this.isEditing = false
      this.isUpdating = false
      this.editingModal = ''
      this.modal = initialModal()
    },
  },
}
</script>

<style lang="scss">
.locations-gmb-id {
  &__header {
    &__cta {
      margin-left: $gutter-mobile;

      @media (min-width: $screen-sm) {
        margin-left: $gutter-tablet;
      }

      @media (min-width: $screen-xl) {
        margin-left: $gutter-desktop;
      }
    }

    &__link-to-location {
      &__button {
        position: fixed;
        right: $gutter-mobile;
        bottom: calc(#{$navigation-height} + #{$gutter-mobile});
        z-index: 2;

        @media (min-width: $screen-sm) {
          position: relative;
          right: initial;
          bottom: initial;
          margin-left: $gutter-tablet;
        }

        @media (min-width: $screen-xl) {
          margin-left: $gutter-desktop;
        }
      }
    }

    &__popover {
      min-width: 280px;

      @media (min-width: $screen-sm) {
        min-width: 340px;
      }

      &__link {
        display: flex;
        align-items: center;
        padding: 0 $gutter-mobile;
        height: 48px;
        color: var(--text-color);

        @media (min-width: $screen-sm) {
          padding: 0 $gutter-tablet;
        }

        &:hover {
          background-color: var(--bg-color-hover);
          cursor: pointer;
        }

        &:first-child {
          border-top-left-radius: $radius-default;
          border-top-right-radius: $radius-default;
        }

        &:last-child {
          border-bottom-left-radius: $radius-default;
          border-bottom-right-radius: $radius-default;
        }

        &__icon {
          margin-right: $gutter-mobile;
        }
      }
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: $gutter-mobile;
    margin-bottom: $gutter-mobile / 2;
    width: 100%;

    @media (min-width: $screen-sm) {
      margin-top: $gutter-tablet;
      margin-bottom: $gutter-tablet / 2;
    }

    @media (min-width: $screen-xl) {
      margin-top: $gutter-desktop;
      margin-bottom: $gutter-desktop / 2;
    }
  }
}
</style>
